import { Component, OnDestroy, OnInit } from '@angular/core';
import type { Breadcrumb } from '../../../core/models/breadcrumb.model';
import { filter, map, merge, Observable, of, Subscription, takeUntil, timer } from 'rxjs';
import type { TreeEntity } from '../../../core/models/entity.model';
import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import {
  selectProjectEntityLoading,
  selectProjectEntityTree,
  selectProjectFeedbackMessages
} from '../../store/project-data/project-data.selectors';
import { MessageLevel } from 'src/app/shared/constants/message-levels';
import { EntityTypeKey, isHood } from 'src/app/shared/constants/entity-type-keys';

@Component({
  selector: 'app-project-prices-view',
  templateUrl: './project-prices-view.component.html',
  styleUrls: ['./project-prices-view.component.scss'],
  standalone: false
})
export class ProjectPricesViewComponent implements OnInit, OnDestroy {
  projectEntity$: Observable<TreeEntity>;
  loading$: Observable<boolean>;
  protected feedbackMessages$: Observable<TreeEntity[]>;

  private routeParamsSubscription: Subscription;

  projectId = '';
  showLoading$ = of(false);

  breadcrumbs: Breadcrumb[] = [];

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.projectEntity$ = this.store.select(selectProjectEntityTree);
    this.loading$ = this.store.select(selectProjectEntityLoading);

    this.routeParamsSubscription = this.route.paramMap.subscribe(params => {
      const projectId = params.get('id');
      if (this.projectId !== projectId) {
        this.projectId = projectId;
        this.loadProject();
      }
    });

    this.feedbackMessages$ = this.store.select(selectProjectFeedbackMessages);

    const resultReceived = this.loading$.pipe(filter(result => !result));

    this.showLoading$ = merge(
      timer(350).pipe(
        map(() => true),
        takeUntil(resultReceived)
      ),
      resultReceived.pipe(map(() => false))
    );
  }

  ngOnInit() {
    this.setBreadcrumbs();
  }

  ngOnDestroy(): void {
    this.routeParamsSubscription?.unsubscribe();
  }

  setBreadcrumbs() {
    this.projectEntity$.subscribe((projectEntity: TreeEntity) => {
      const nameProperty = projectEntity?.Properties['Name'];
      const name = (nameProperty?.Value as string) || '-';

      this.breadcrumbs = [
        { url: '/projects', text: 'UI.Common.Breadcrumb.Projects', translatable: true, isCurrent: false },
        { url: `/projects/${this.projectId}`, text: name, translatable: false, isCurrent: false },
        { url: '', text: 'UI.Common.Breadcrumb.ProjectPrices', translatable: true, isCurrent: true }
      ];
    });
  }

  hasExportSelected(project: TreeEntity): boolean {
    return (
      project &&
      project.Children.some(x => x.EntityTypeKey === EntityTypeKey.GastroFlowHood && (x.Properties['IncludeInExports'].Value as boolean))
    );
  }

  private loadProject(): void {
    this.store.dispatch(ProjectDataActions.get_project_start({ id: this.projectId }));
  }

  protected disablePyramid(messages: TreeEntity[], projectEntity: TreeEntity): boolean {
    return this.hasErrors(messages) || this.checkFireExtinguishing(projectEntity);
  }

  protected getReportProjectPrices() {
    this.store.dispatch(ProjectDataActions.get_report_start({ reportType: 'Prices', extension: 'xls' }));
  }

  protected exportProjectToPyramid() {
    this.store.dispatch(ProjectDataActions.export_project_to_pyramid_start());
  }

  private hasErrors(messages: TreeEntity[]): boolean {
    return (
      messages.filter(
        message =>
          message.Properties['Level']?.Value === MessageLevel.Error &&
          message.Parent?.EntityTypeKey !== EntityTypeKey.GastroFlowHoodLine &&
          message.Parent?.EntityTypeKey !== EntityTypeKey.GastroFlowHoodConnectionLine
      ).length > 0
    );
  }

  private checkFireExtinguishing(projectEntity: TreeEntity): boolean {
    const hoods = projectEntity?.Children.filter(child => isHood(child.EntityTypeKey)) ?? [];

    return hoods.filter(hood => hood.Properties['FireExtinguishing']?.Value === 'General' || hood.Properties['FireExtinguishing']?.Value === 'Ansulex' || hood.Properties['FireExtinguishing']?.Value === 'Amerex').length > 0;
  }
}
